/**
 * Chrona Plan wrapper (A2F-492 / A2F-998).
 *
 * Routes the two Plan views (Gantt, Section Map) by URL pathname and shows
 * a guided first-time experience when no submissions are marked as active
 * (isPlanning). Document Signoff and Document Clearance were split out into
 * the standalone Chrona Documents group in A2F-998.
 * The two views are linked from the sidebar nav directly — the in-page
 * tab bar was retired.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ListChecks, Settings2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { PlanningViewPage } from './PlanningViewPage';
import { SubmissionClassificationPage } from './SubmissionClassificationPage';
import { SelectSubmissionsModal } from './SelectSubmissionsModal';
import type { Submission } from '@/types/api';

type PlanTab = 'planning' | 'sections';

export function PlanPage() {
  const { activeProject } = useProject();
  const location = useLocation();
  const projectId = activeProject?.id ?? '';

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [guideDismissed, setGuideDismissed] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const activeTab: PlanTab =
    location.pathname === '/plan/sections' ? 'sections' : 'planning';

  const fetchSubmissions = useCallback(async () => {
    if (!projectId) return;
    try {
      const subs = await api.projects.submissions(projectId, true);
      setSubmissions(subs);
    } catch {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleSetPlanning = useCallback(async (selectedIds: string[]) => {
    if (!projectId) return;
    try {
      await api.projects.setPlanning(projectId, selectedIds);
      setShowSelectModal(false);
      setGuideDismissed(false);
      await fetchSubmissions();
      setRefreshKey(k => k + 1);
    } catch {
      // silently ignore for MVP
    }
  }, [projectId, fetchSubmissions]);

  if (loading) return <LoadingSpinner message="Loading plan..." />;

  if (!activeProject) {
    return (
      <div className="max-w-5xl mx-auto py-16 text-center">
        <p className="text-slate-500">No project selected. Create or select a project in Settings.</p>
      </div>
    );
  }

  const planningCount = submissions.filter(s => s.isPlanning).length;

  // --- No active submissions: guided prompt or empty state ---
  if (planningCount === 0) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
            Chrona Plan
          </p>
          <h1 className="text-xl font-bold text-brand-charcoal">
            Submission Planning
          </h1>
        </div>

        {!guideDismissed ? (
          // Guided dialog
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-md w-full mx-4 shadow-xl">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-brand-orange/10 flex items-center justify-center mx-auto">
                  <ListChecks className="w-6 h-6 text-brand-orange" />
                </div>
                <h2 className="text-lg font-bold text-brand-charcoal">
                  Select your active submissions to get started
                </h2>
                <p className="text-sm text-slate-500">
                  Choose which submissions you're actively working on.
                  Gantt Chart and Section Map will be scoped to your selection.
                </p>
                <div className="flex justify-center gap-3 pt-2">
                  <button
                    onClick={() => setGuideDismissed(true)}
                    className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setGuideDismissed(true);
                      setShowSelectModal(true);
                    }}
                    className="px-5 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Empty state fallback
          <div className="flex items-center justify-center py-20 bg-white rounded-xl border border-slate-200">
            <div className="text-center">
              <p className="text-sm font-medium text-slate-500">No submissions selected for planning</p>
              <p className="text-xs text-slate-400 mt-1">Choose which submissions to track on the timeline.</p>
              <button
                onClick={() => setShowSelectModal(true)}
                className="mt-4 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-brand-orange/90 transition-colors"
              >
                Select Submissions
              </button>
            </div>
          </div>
        )}

        {showSelectModal && (
          <SelectSubmissionsModal
            submissions={submissions}
            onApply={handleSetPlanning}
            onClose={() => setShowSelectModal(false)}
          />
        )}
      </div>
    );
  }

  // --- Has active submissions: show content ---
  // Tabs were dropped now that the two Plan views live in the sidebar nav;
  // we keep the Active Submissions badge + Manage button as a compact header.
  return (
    <div className="space-y-0">
      <div className="flex items-center justify-end gap-2 mb-6">
        <span className="text-[10px] font-bold text-brand-charcoal bg-slate-100 px-2.5 py-1 rounded-full">
          {planningCount} ACTIVE SUBMISSION{planningCount !== 1 ? 'S' : ''}
        </span>
        <button
          onClick={() => setShowSelectModal(true)}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-brand-orange transition-colors"
        >
          <Settings2 className="w-3.5 h-3.5" />
          Manage
        </button>
      </div>

      {/* Tab content — key forces remount after Manage applies changes */}
      {activeTab === 'planning' && <PlanningViewPage key={refreshKey} />}
      {activeTab === 'sections' && <SubmissionClassificationPage key={refreshKey} />}

      {showSelectModal && (
        <SelectSubmissionsModal
          submissions={submissions}
          onApply={handleSetPlanning}
          onClose={() => setShowSelectModal(false)}
        />
      )}
    </div>
  );
}
